<template>
  <div>
    <button class="button summary" @click="showModal()">
      {{ $t('common.viewStats') }}
    </button>
    <Modal
      v-model="isModalVisible"
      :title="$t('chat.greatWork')"
      :dialog-class="'modal-dialog-centered'"
    >
      <ConfettiCanvas ref="confetti" />
      <div class="stats-container">
        <div class="stat-card stat-card--done">
          <div class="stat-icon stat-icon--done">⏱️</div>
          <div class="stat-content">
            <h6 class="stat-label">{{ $t('chat.youSpoke') }}</h6>
            <p class="stat-value stat-value--done">{{ chatDuration }}</p>
            <p class="stat-subtitle">{{ $t('chat.thisChat') }}</p>
          </div>
        </div>

        <div class="stat-card stat-card--achievement">
          <div class="stat-icon stat-icon--achievement">🏆</div>
          <div class="stat-content">
            <h6 class="stat-label">{{ $t('chat.lifetimeSpoken') }}</h6>
            <p class="stat-value stat-value--achievement">{{ lifetimeDuration }}</p>
            <p class="stat-subtitle">{{ $t('chat.keepItUp') }}</p>
          </div>
        </div>
      </div>
      <template #footer>
        <div class="d-flex justify-content-center w-100">
          <button class="button new-chat" @click="emitNewChatRequest">{{ $t('chat.newChat') }}</button>
        </div>
      </template>
    </Modal>
  </div>
</template>

<script>
import Modal from '../Modal.vue';
import ConfettiCanvas from '../ConfettiCanvas.vue';
import { getDatabase, ref, query, get } from "firebase/database";

export default {
  name: "ChatFinishedStats",
  components: {
    Modal,
    ConfettiCanvas
  },
  props: {
    userId: String,
    dialogId: String,
  },
  data() {
    return {
      chatDuration: "...",
      lifetimeDuration: "...",
      isModalVisible: false,
    };
  },
  methods: {
    showModal() {
      this.isModalVisible = true;
      this.$nextTick(() => {
        this.$refs.confetti.launchConfetti();
      });
      this.getStats();
    },
    getStats() {
      this.getChatDuration();
      this.getLifetimeChatDuration();
    },
    async getChatDuration() {
      const db = getDatabase();
      const messagesSnapshot = await get(query(ref(db, "messages/" + this.dialogId)));
      let spokenSeconds = 0;
      const messages = messagesSnapshot.val();
      console.log(messages);

      const audioPromises = Object.values(messages).map(async (message) => {
        if (message.speakerId === "student") {
          const audioSnapshot = await get(query(ref(db, "audio/" + message.audioId)));
          const audio = audioSnapshot.val();
          return audio.durationSeconds;
        }
        return 0;
      });

      const audioDurations = await Promise.all(audioPromises);
      spokenSeconds = audioDurations.reduce((total, duration) => total + duration, 0);

      this.chatDuration = this.formatSeconds(spokenSeconds);
    },
    // Reads the counter leaf, not the language node: stats/{uid}/{lang} also
    // carries the Voice Ledger's daily map, which grows a child per day of
    // practice. Pulling all of it down for one number would cost more every
    // year, and .val() on a learner with no stats yet is null, which used to
    // throw here.
    getLifetimeChatDuration() {
      const db = getDatabase();
      get(query(ref(db, "dialog-meta/" + this.dialogId))).then((dialogMetaSnapshot) => {
        const chatLangCode = dialogMetaSnapshot.val()?.lang;
        if (!chatLangCode) return;
        const lifetimeRef = ref(
          db,
          "stats/" + this.userId + "/" + chatLangCode + "/lifetimeSpokenSeconds"
        );
        get(lifetimeRef).then((lifetimeSnapshot) => {
          this.lifetimeDuration = this.formatSeconds(lifetimeSnapshot.val() || 0);
        });
      });
    },
    formatSeconds(seconds) {
      const days = Math.floor(seconds / (24 * 3600));
      seconds %= 24 * 3600;
      const hours = Math.floor(seconds / 3600);
      seconds %= 3600;
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = Math.round(seconds % 60);

      const parts = [];
      if (days > 0) parts.push(`${days} days`);
      if (hours > 0) parts.push(`${hours} hours`);
      if (minutes > 0) parts.push(`${minutes} minutes`);
      if (remainingSeconds > 0) parts.push(`${remainingSeconds} seconds`);

      return parts.join(', ');
    },
    emitNewChatRequest() {
      this.isModalVisible = false;
      this.$emit("newChatRequest");
    },
  },
};
</script>

<style scoped>
:deep(.modal-title) {
  text-align: center;
  width: 100%;
}

.modal-dialog {
  max-width: 450px;
  margin: 1.75rem auto;
}

.modal-content {
  border: none;
  border-radius: 1rem;
  box-shadow: var(--al-shadow-md);
  overflow: hidden;
}

.modal-header {
  border-bottom: none;
  padding: 1.5rem 1.5rem 0.5rem;
  position: relative;
}


.modal-body {
  padding: 0.5rem 1.5rem 1rem;
}

.modal-footer {
  border-top: none;
  padding: 0.5rem 1.5rem 1.5rem;
  justify-content: center;
}

.stats-container {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.stat-card {
  display: flex;
  align-items: center;
  background-color: var(--al-surface-sunken);
  border-radius: 0.75rem;
  padding: 1rem;
  box-shadow: var(--al-shadow-sm);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: var(--al-shadow-md);
}

.stat-icon {
  font-size: 2rem;
  margin-right: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 50%;
  flex-shrink: 0;
}

/* "This chat" is a done stat (session just completed): green semantics. */
.stat-icon--done {
  background-color: color-mix(in srgb, var(--al-success) 15%, var(--al-surface-raised));
}

/* Lifetime total is an ongoing achievement/streak stat: warm semantics. */
.stat-icon--achievement {
  background-color: var(--al-warm-soft);
}

.stat-content {
  flex: 1;
}

.stat-label {
  font-size: 0.9rem;
  color: var(--al-text-muted);
  margin-bottom: 0.25rem;
  font-weight: 500;
}

.stat-value {
  font-size: 1.25rem;
  font-weight: 700;
  margin-bottom: 0.25rem;
  line-height: 1.2;
}

/* AA: --al-success on white is ~2.5:1 (fails even for large bold text), so
   the "done" stat value uses --al-success-dark (~3.8:1) instead. */
.stat-value--done {
  color: var(--al-success-dark);
}

/* AA: white/orange text on --al-orange fails; warm elements use the
   primary-text role (ink on light, near-white on dark). */
.stat-value--achievement {
  color: var(--al-text);
}

.stat-subtitle {
  font-size: 0.85rem;
  color: var(--al-text-muted);
  margin-bottom: 0;
}

.button.new-chat,
.button.summary {
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
  text-align: center;
  border: none;
  border-radius: 0.5rem;
  color: white;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  cursor: pointer;
  font-family: inherit;
  font-weight: 600;
}

.button.new-chat:focus-visible,
.button.summary:focus-visible {
  outline: 0;
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--al-primary) 30%, transparent);
}

/* Both are neutral "continue" actions (not achievements), so per the
   primary-CTA rule they map to primary blue rather than the old orange. */
.button.new-chat {
  background-color: var(--al-primary);
  width: 100%;
  max-width: 200px;
}

@media (max-width: 768px) {
  .button.new-chat {
    max-width: 240px;
    padding: 1rem 1.75rem;
    font-size: 1.2rem;
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
  }
}

.button.summary {
  background-color: var(--al-primary);
}

.button.new-chat:hover,
.button.summary:hover {
  background-color: var(--al-primary-hover);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
  transform: translateY(-2px);
}

.button.new-chat:active,
.button.summary:active {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transform: translateY(0);
}

/* Responsive styles */
@media (min-width: 576px) {
  .stats-container {
    flex-direction: row;
  }

  .stat-card {
    flex: 1;
  }
}

@media (max-width: 576px) {
  .modal-dialog {
    margin: 1rem;
  }

  .modal-title {
    font-size: 1.25rem;
  }

  .stat-icon {
    font-size: 1.5rem;
    width: 3rem;
    height: 3rem;
  }

  .stat-value {
    font-size: 1.1rem;
  }
}
</style>